import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const fieldLabel = "block text-xs font-medium uppercase tracking-[0.08em] text-zinc-400 dark:text-zinc-500 mb-1";
const textarea = "w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder-zinc-400 focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30 dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-zinc-100";

export default function ArtistProfileForm({
  profileForm,
  isProfileDirty,
  isSavingProfile,
  isProfileLoading,
  artistProfile,
  profileGenresList,
  profileInstrumentsList,
  profileLinksList,
  onSubmit,
  onFieldChange,
}) {
  return (
    <section className="panel p-6">
      <div className="flex flex-col gap-6 lg:flex-row">
        <form className="flex-1 space-y-4" onSubmit={onSubmit}>
          <div>
            <label className={fieldLabel} htmlFor="profile-bio">Stage bio</label>
            <textarea
              id="profile-bio"
              value={profileForm.bio}
              onChange={onFieldChange("bio")}
              rows={4}
              className={textarea}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={fieldLabel} htmlFor="profile-experience">Experience level</label>
              <Input
                id="profile-experience"
                value={profileForm.experienceLevel}
                onChange={onFieldChange("experienceLevel")}
                placeholder="e.g. Touring artist"
              />
            </div>
            <div>
              <label className={fieldLabel} htmlFor="profile-website">Website</label>
              <Input
                id="profile-website"
                value={profileForm.website}
                onChange={onFieldChange("website")}
                placeholder="https://"
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={fieldLabel} htmlFor="profile-genres">Genres (comma separated)</label>
              <Input
                id="profile-genres"
                value={profileForm.genres}
                onChange={onFieldChange("genres")}
                placeholder="Jazz, Soul, Funk"
              />
            </div>
            <div>
              <label className={fieldLabel} htmlFor="profile-instruments">Instruments / setup</label>
              <Input
                id="profile-instruments"
                value={profileForm.instruments}
                onChange={onFieldChange("instruments")}
                placeholder="Saxophone, Vocals"
              />
            </div>
          </div>
          <div>
            <label className={fieldLabel} htmlFor="profile-social">Social links (comma separated)</label>
            <Input
              id="profile-social"
              value={profileForm.socialLinks}
              onChange={onFieldChange("socialLinks")}
              placeholder="https://instagram.com/yourband, https://spotify.com/..."
            />
          </div>
          <div>
            <label className={fieldLabel} htmlFor="profile-cover">Cover image URL</label>
            <Input
              id="profile-cover"
              value={profileForm.coverImageUrl}
              onChange={onFieldChange("coverImageUrl")}
              placeholder="https://images.unsplash.com/..."
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="submit"
              className="btn-primary"
              disabled={isSavingProfile || !isProfileDirty}
            >
              {isSavingProfile ? "Saving..." : "Save profile"}
            </Button>
            {isProfileLoading && (
              <span className="text-xs text-zinc-400">Loading profile...</span>
            )}
            {!isProfileDirty && !isProfileLoading && artistProfile?.updated_at && (
              <span className="text-xs text-zinc-400">
                Last updated {new Date(artistProfile.updated_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </form>

        <div className="panel-strong w-full max-w-sm space-y-4 p-5 text-sm">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
            Profile preview
          </h3>
          <div className="space-y-3 text-zinc-600 dark:text-zinc-300">
            <div>
              <span className="block text-[11px] uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Cover image</span>
              {profileForm.coverImageUrl ? (
                <div
                  className="mt-1.5 h-32 w-full rounded-lg border border-zinc-200/60 bg-cover bg-center dark:border-white/[0.07]"
                  style={{ backgroundImage: `url(${profileForm.coverImageUrl})` }}
                />
              ) : (
                <p className="mt-1 text-xs text-zinc-400 dark:text-zinc-500">Add a photo to showcase your act.</p>
              )}
            </div>
            <div>
              <span className="block text-[11px] uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Bio</span>
              <p className="mt-1 whitespace-pre-line text-zinc-700 dark:text-zinc-300">
                {profileForm.bio || "Add a short description about your act."}
              </p>
            </div>
            <div>
              <span className="block text-[11px] uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Experience</span>
              <p className="mt-1 text-zinc-700 dark:text-zinc-300">
                {profileForm.experienceLevel || "Let venues know where you shine."}
              </p>
            </div>
            <div>
              <span className="block text-[11px] uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Genres</span>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {profileGenresList.length > 0 ? (
                  profileGenresList.map((genre) => (
                    <span
                      key={`profile-genre-${genre}`}
                      className="inline-flex rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                    >
                      {genre}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-zinc-400 dark:text-zinc-500">Add genres so venues can find you.</span>
                )}
              </div>
            </div>
            <div>
              <span className="block text-[11px] uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Setup</span>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {profileInstrumentsList.length > 0 ? (
                  profileInstrumentsList.map((item) => (
                    <span
                      key={`profile-instrument-${item}`}
                      className="inline-flex rounded-md border border-zinc-200 px-2 py-0.5 text-xs text-zinc-600 dark:border-white/[0.08] dark:text-zinc-300"
                    >
                      {item}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-zinc-400 dark:text-zinc-500">Share what you bring on stage.</span>
                )}
              </div>
            </div>
            <div>
              <span className="block text-[11px] uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">Links</span>
              <div className="mt-1 flex flex-col gap-1">
                {profileLinksList.length > 0 ? (
                  profileLinksList.map((link) => (
                    <span
                      key={`profile-link-${link}`}
                      className="truncate text-xs text-amber-600 dark:text-amber-400"
                    >
                      {link}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-zinc-400 dark:text-zinc-500">Share socials or music profiles.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
